import React from "react";
import { X, MapPin, Clock, Package } from "lucide-react";

interface Checkpoint {
  checkpoint?: string;
  date?: string;
  code?: string;
  station?: {
    name: string;
    address?: string;
    city?: string;
    phone?: string;
    postalCode?: string;
  } | null;
  dateTime?: string;
  action?: string;
  location?: string;
  notes?: string;
}

interface TrackingData {
  voucherNumber: string;
  checkpoints: Checkpoint[];
  deliveryDate?: string;
  deliveryTime?: string;
  deliveryStation?: string;
  collected?: boolean;
  receivedBy?: string;
  weight?: number;
  lastUpdate?: string;
  // ACS summary fields
  summary?: {
    status?: string;
    statusMessage?: string;
    deliveryFlag?: number;
    returnedFlag?: number;
    shipmentStatus?: number;
    nonDeliveryReasonCode?: string;
    deliveryInfo?: string;
    pickupDate?: string;
    deliveryDateExpected?: string;
    sender?: string;
    recipient?: string;
    recipientAddress?: string;
    stationOrigin?: string;
    stationDestination?: string;
  };
}

interface TrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackingData: TrackingData | null;
  shippingCompany: string;
  loading: boolean;
  error: string | null;
}

const TrackingModal: React.FC<TrackingModalProps> = ({
  isOpen,
  onClose,
  trackingData,
  shippingCompany,
  loading,
  error,
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";

    // Handle EasyMail date format: /Date(1741796008977+0200)/
    if (dateString.startsWith("/Date(")) {
      const timestamp = parseInt(dateString.match(/\d+/)?.[0] || "0");
      return new Date(timestamp).toLocaleString();
    }

    return new Date(dateString).toLocaleString();
  };

  const getShipmentStatusName = (code: number) => {
    const statusMap: { [key: number]: string } = {
      1: "Non-Delivery (Refusal)",
      2: "Unknown Consignee / In Transit",
      3: "Absent / Rescheduled",
      4: "Delivered",
      5: "Rescheduled / Special Handling",
      6: "To Be Returned",
      7: "Returned to Sender",
    };
    return statusMap[code] || `Status ${code}`;
  };

  const getNonDeliveryReasonName = (code: string) => {
    const reasonMap: { [key: string]: string } = {
      AD1: "Office Pickup",
      AD3: "Force Majeure",
      AD8: "Pick-up from ACS Shop",
      AP1: "Delivery Refusal (charge)",
      AP2: "Unable to Pay",
      AP3: "Not Acceptable Shipment",
      AP4: "Deceased",
      AS1: "Absent",
      DP1: "Difficult to Access Area",
      LS1: "Unknown Consignee",
      LS2: "In Transit to Destination",
      LS3: "Error or Inadequate Address",
      PA1: "Rescheduled by Shipper",
      PA2: "Rescheduled by Consignee",
      PA4: "Reschedule / Redirect",
    };
    return reasonMap[code] || code;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

        {/* Modal */}
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Fixed at top */}
          <div
            className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0"
            style={{ backgroundColor: "#264a3e" }}
          >
            <div className="flex items-center space-x-3">
              <Package className="w-6 h-6 text-white" />
              <h2 className="text-xl font-semibold text-white">
                Tracking Information
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="p-6 flex-1 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-b-2"
                  style={{ borderColor: "#264a3e" }}
                ></div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {!loading && !error && trackingData && (
              <>
                {/* Summary */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Tracking Number</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {trackingData.voucherNumber}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Shipping Company</p>
                      <p className="text-lg font-semibold text-gray-900 uppercase">
                        {shippingCompany}
                      </p>
                    </div>

                    {/* ACS Summary Status */}
                    {trackingData.summary && (
                      <>
                        <div className="col-span-2">
                          <p className="text-sm text-gray-500">Status</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                trackingData.summary.deliveryFlag === 1
                                  ? "bg-green-100 text-green-800"
                                  : trackingData.summary.returnedFlag === 1
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {trackingData.summary.statusMessage || "Unknown"}
                            </span>
                            {trackingData.summary.shipmentStatus && (
                              <span className="text-xs text-gray-500">
                                ({getShipmentStatusName(trackingData.summary.shipmentStatus)})
                              </span>
                            )}
                          </div>
                        </div>

                        {trackingData.summary.deliveryInfo && (
                          <div className="col-span-2">
                            <p className="text-sm text-gray-500">Delivery Info</p>
                            <p className="text-sm text-gray-900 mt-1">
                              {trackingData.summary.deliveryInfo}
                            </p>
                          </div>
                        )}

                        {trackingData.summary.nonDeliveryReasonCode && (
                          <div className="col-span-2">
                            <p className="text-sm text-gray-500">Non-Delivery Reason</p>
                            <p className="text-sm font-medium text-orange-700 mt-1">
                              {getNonDeliveryReasonName(trackingData.summary.nonDeliveryReasonCode)}
                            </p>
                          </div>
                        )}

                        {trackingData.summary.recipient && (
                          <div>
                            <p className="text-sm text-gray-500">Recipient</p>
                            <p className="text-sm text-gray-900 mt-1">
                              {trackingData.summary.recipient}
                            </p>
                            {trackingData.summary.recipientAddress && (
                              <p className="text-xs text-gray-600">
                                {trackingData.summary.recipientAddress}
                              </p>
                            )}
                          </div>
                        )}

                        {trackingData.summary.sender && (
                          <div>
                            <p className="text-sm text-gray-500">Sender</p>
                            <p className="text-sm text-gray-900 mt-1">
                              {trackingData.summary.sender}
                            </p>
                          </div>
                        )}

                        {trackingData.summary.stationOrigin && (
                          <div>
                            <p className="text-sm text-gray-500">Origin Station</p>
                            <p className="text-sm text-gray-900 mt-1">
                              {trackingData.summary.stationOrigin}
                            </p>
                          </div>
                        )}

                        {trackingData.summary.stationDestination && (
                          <div>
                            <p className="text-sm text-gray-500">Destination Station</p>
                            <p className="text-sm text-gray-900 mt-1">
                              {trackingData.summary.stationDestination}
                            </p>
                          </div>
                        )}

                        {trackingData.summary.deliveryDateExpected && (
                          <div>
                            <p className="text-sm text-gray-500">Expected Delivery</p>
                            <p className="text-sm text-gray-900 mt-1">
                              {formatDate(trackingData.summary.deliveryDateExpected)}
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {/* EasyMail Fields */}
                    {trackingData.deliveryDate && !trackingData.summary && (
                      <div>
                        <p className="text-sm text-gray-500">Delivery Date</p>
                        <p className="text-base font-medium text-gray-900">
                          {trackingData.deliveryDate}{" "}
                          {trackingData.deliveryTime}
                        </p>
                      </div>
                    )}
                    {trackingData.receivedBy && (
                      <div>
                        <p className="text-sm text-gray-500">Received By</p>
                        <p className="text-base font-medium text-gray-900">
                          {trackingData.receivedBy}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Checkpoints Timeline */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Tracking History
                  </h3>

                  {trackingData.checkpoints &&
                  trackingData.checkpoints.length > 0 ? (
                    <div className="space-y-4">
                      {trackingData.checkpoints.map((checkpoint, index) => (
                        <div
                          key={index}
                          className="flex items-start space-x-4 border-l-2 pl-4 pb-4"
                          style={{
                            borderColor: index === 0 ? "#264a3e" : "#e5e7eb",
                          }}
                        >
                          <div
                            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1"
                            style={{
                              backgroundColor:
                                index === 0 ? "#264a3e" : "#f3f4f6",
                              color: index === 0 ? "white" : "#6b7280",
                            }}
                          >
                            {index === 0 ? (
                              <MapPin className="w-4 h-4" />
                            ) : (
                              <Clock className="w-4 h-4" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900">
                                {checkpoint.checkpoint ||
                                  checkpoint.action ||
                                  "Checkpoint"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatDate(
                                  checkpoint.date || checkpoint.dateTime || ""
                                )}
                              </p>
                            </div>
                            {(checkpoint.location || checkpoint.station) && (
                              <p className="text-sm text-gray-600 mt-1">
                                <MapPin className="w-3 h-3 inline mr-1" />
                                {checkpoint.location ||
                                  checkpoint.station?.name}
                                {checkpoint.station?.city &&
                                  ` - ${checkpoint.station.city}`}
                              </p>
                            )}
                            {(checkpoint.notes ||
                              checkpoint.station?.address) && (
                              <p className="text-xs text-gray-500 mt-1">
                                {checkpoint.notes ||
                                  checkpoint.station?.address}
                              </p>
                            )}
                            {checkpoint.code && (
                              <p className="text-xs text-gray-400 mt-1">
                                Code: {checkpoint.code}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>No tracking checkpoints available yet</p>
                    </div>
                  )}
                </div>

                {/* Last Update */}
                {trackingData.lastUpdate && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      Last updated: {formatDate(trackingData.lastUpdate)}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer - Fixed at bottom */}
          <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-colors"
              style={{ backgroundColor: "#264a3e" }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingModal;
